import React, { useState, useEffect } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import usePrevious from 'use-previous';
import classNames from 'classnames';
import isEqual from 'fast-deep-equal';
import { AutosaveTextarea, Checkbox } from '@fikasio/react-ui-components';
import DropdownToggle from 'components/UI/DropdownToggle';
import TrashIcon from 'components/UI/TrashIcon';
import { usePatchOrganizationMutation, useDeleteOrganizationMutation } from 'services/organizations/api';
import { Organization } from 'services/organizations/types';

export interface OrganizationRowProps {
  isSelected?: boolean;
  onClick: (organizationId: string) => Promise<void>;
  onError?: (message: string | null) => void;
  onSelect: (organization: Organization) => Promise<void>;
  organization: Organization;
}

const OrganizationRow = ({
  isSelected,
  onClick,
  onError,
  onSelect,
  organization: pOrganization,
}: OrganizationRowProps) => {
  const { t } = useTranslation();

  const prevPOrganization = usePrevious(pOrganization);
  const [organization, setOrganization] = useState(pOrganization);
  const [name, setName] = useState((organization && organization.name) || '');
  const [hasFocus, setHasFocus] = useState(false);

  const [patchOrganization] = usePatchOrganizationMutation();
  const [deleteOrganization] = useDeleteOrganizationMutation();

  useEffect(() => {
    if (!isEqual(pOrganization, prevPOrganization)) {
      setOrganization({
        ...organization,
        ...pOrganization,
      });
    }
  }, [pOrganization]);

  useEffect(() => {
    if (!hasFocus) {
      setName(organization.name || '');
    }
  }, [organization]);

  const handleBlur = () => {
    setHasFocus(false);
  };

  const handleFocus = () => {
    setHasFocus(true);
  };

  return (
    <tr
      className={classNames({
        organizationRow: true,
        selected: isSelected,
      })}
    >
      <td
        style={{
          textAlign: 'center',
        }}
        width={35}
      >
        <Checkbox
          isChecked={isSelected}
          onClick={() => onSelect(organization)}
        />
      </td>
      <td
        className="organizationRow_name"
        onClick={() => onClick && onClick(organization.id)}
        style={{
          cursor: 'pointer',
        }}
      >
        <AutosaveTextarea
          className={classNames({
            organizationRow_name_editable: true,
            [organization.id]: true,
          })}
          defaultValue={name}
          onBlur={() => handleBlur()}
          onFocus={() => handleFocus()}
          onSave={async value => {
            if (organization.id) {
              patchOrganization({
                id: organization.id,
                name: value,
              });
            }
          }}
          style={{
            float: 'left',
            height: 25,
            overflowY: 'hidden',
            paddingLeft: 5,
            paddingRight: 50,
            paddingTop: 0,
            width: 'auto',
          }}
          useContentEditableDiv
        />
      </td>
      <td
        style={{
          textAlign: 'center',
        }}
        width={35}
      >
        <Dropdown
          style={{
            position: 'static',
          }}
        >
          <Dropdown.Toggle as={DropdownToggle} />
          <Dropdown.Menu>
            <Dropdown.Item
              onClick={async () => {
                try {
                  await deleteOrganization(organization.id).unwrap();
                  onError?.(null);
                } catch (error) {
                  const status = typeof error === 'object' && error !== null && 'status' in error
                    ? (error as { status: unknown }).status
                    : error;
                  onError?.(
                    status === 409
                      ? t('cannotDeleteLastOrganization')
                      : t('organizationDeleteFailed'),
                  );
                }
              }}
            >
              <TrashIcon
                style={{
                  color: 'red',
                  marginRight: 10,
                  width: 25,
                }}
              />
              {t('delete')}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </td>
    </tr>
  );
};

export default OrganizationRow;
